import { useEffect, useState, type ChangeEvent, type FormEvent } from "react";
import { useForm } from "../useForm";

import InputTextOnly from "../../controls/InputTextOnly";
import { classNames } from "../../../shared/utility";
import SelectMenu from "../../controls/SelectMenu";
import { XMarkIcon } from "@heroicons/react/24/outline";
import type { SelectOption } from "../../../types/api";
import type {
  ResetUserPasswordPayload,
  UserRecord,
  UserStatusValue,
  UserTypeValue,
} from "../../../types/users";

const userTypeOptions: Array<SelectOption<UserTypeValue>> = [
  { value: 0, label: "Sales Rep" },
  { value: 1, label: "Administrator" },
  { value: 2, label: "Customer" },
];

const statusOptions: Array<SelectOption<UserStatusValue>> = [
  { value: 0, label: "Active" },
  { value: 1, label: "Deleted" },
];

const initialFValues = {
  first_name: "",
  last_name: "",
  email: "",
  type: userTypeOptions[0],
  phone_number: "",
  status: statusOptions[0],
  password: "",
  password_confirmation: "",
};

export type UserFormValues = typeof initialFValues & { user_business?: SelectOption<number> | null };
type UserOption = SelectOption<UserTypeValue> | SelectOption<UserStatusValue>;

const optionValue = <TOption extends UserOption>(
  selected: TOption | TOption["value"] | "" | null | undefined,
  options: readonly TOption[],
): TOption | null => {
  if (selected == null || selected === "") return null;
  if (typeof selected === "object") {
    return options.find((option) => String(option.value) === String(selected.value)) ?? selected;
  }
  return options.find((option) => String(option.value) === String(selected)) ?? null;
};

interface UserDetailsFormProps {
  user?: UserRecord | null;
  index?: number;
  actionCall: (values: UserFormValues) => void | Promise<void>;
  isEdit?: boolean | string;
  onResetPassword?: (values: ResetUserPasswordPayload) => void | Promise<void>;
  panelMode?: boolean;
  onCancel?: () => void;
}

export default function UserDetailsForm(props: UserDetailsFormProps) {
  const { user, actionCall, isEdit, onResetPassword, panelMode, onCancel } = props;

  const [showResetModal, setShowResetModal] = useState(false);
  const [resetValues, setResetValues] = useState({
    password: "",
    password_confirmation: "",
  });
  const [resetErrors, setResetErrors] = useState<Record<keyof ResetUserPasswordPayload, string | null>>({
    password: null,
    password_confirmation: null,
  });

  const validate = (fieldValues: Partial<UserFormValues> = values) => {
    let temp = { ...errors };

    temp.first_name = null;
    if (values.first_name === "") {
      temp.first_name = "Please fill out field.";
    }

    temp.last_name = null;
    if (values.last_name === "") {
      temp.last_name = "Please fill out field.";
    }

    temp.email = null;
    if (values.email === "") {
      temp.email = "Please fill out field.";
    }

    setErrors({
      ...temp,
    });

    if (fieldValues === values) {
      return Object.values(temp).every((x) => x === null);
    }
  };

  const { values, setValues, errors, setErrors, handleInputChange } = useForm(
    initialFValues,
    false
  );

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (validate()) {
      void actionCall(values);
    }
  };

  const openResetModal = () => {
    setResetValues({ password: "", password_confirmation: "" });
    setResetErrors({ password: null, password_confirmation: null });
    setShowResetModal(true);
  };

  const handleResetChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setResetValues({ ...resetValues, [name]: value });
  };

  const validateReset = () => {
    let temp = { ...resetErrors };
    temp.password = null;
    temp.password_confirmation = null;

    if (!resetValues.password || resetValues.password.length < 6) {
      temp.password = "Password must be at least 6 characters.";
    }

    if (resetValues.password_confirmation !== resetValues.password) {
      temp.password_confirmation = "Passwords do not match.";
    }

    setResetErrors(temp);
    return Object.values(temp).every((x) => x === null);
  };

  const handleConfirmReset = () => {
    if (!validateReset()) return;
    if (typeof onResetPassword === "function") {
      void onResetPassword({ ...resetValues });
    }
    setShowResetModal(false);
  };

  useEffect(() => {
    if (user) {
      setValues((prev) => ({
        ...prev,
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        phone_number: user.phone_number ?? "",
        type: user.type,
        status: user.status,
        user_business: user.user_business ?? null,
      }));
    }
  }, [user, setValues]);

  return (
    <form onSubmit={handleSubmit} className={panelMode ? "flex h-full flex-col bg-white" : ""}>
      {panelMode && (
        <div className="flex items-start justify-between border-b border-gray-200 px-6 py-5">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              {isEdit ? "Edit user" : "Add user"}
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Manage account details, access, and status.
            </p>
          </div>
          <button
            type="button"
          onClick={onCancel}
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100"
            aria-label="Close panel"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      )}
      <div className={panelMode ? "flex-1 overflow-y-auto p-6" : "p-4"}>
        <div className="space-y-3">
          <div>
            <div className="grid grid-cols-2 gap-4 max-w-4xl">
              <div>
                <InputTextOnly
                  label="First name"
                  name="first_name"
                  onChange={handleInputChange}
                  value={values.first_name}
                  labelOn={true}
                  error={errors.first_name ?? undefined}
                />
              </div>
              <div>
                <InputTextOnly
                  label="Last name"
                  name="last_name"
                  onChange={handleInputChange}
                  value={values.last_name}
                  labelOn={true}
                  error={errors.last_name ?? undefined}
                />
              </div>
              <div>
                <InputTextOnly
                  label="Email"
                  name="email"
                  onChange={handleInputChange}
                  value={values.email}
                  labelOn={true}
                  error={errors.email ?? undefined}
                />
              </div>
              <div>
                <InputTextOnly
                  label="Phone number"
                  name="phone_number"
                  onChange={handleInputChange}
                  value={values.phone_number}
                  labelOn={true}
                  error={errors.phone_number ?? undefined}
                />
              </div>
              <div>
                <SelectMenu
                  handleInputChange={handleInputChange}
                  name="type"
                  value={optionValue(values.type, userTypeOptions)}
                  options={userTypeOptions}
                  title={"Access type"}
                />
              </div>
              <div>
                <SelectMenu
                  handleInputChange={handleInputChange}
                  name="status"
                  value={optionValue(values.status, statusOptions)}
                  options={statusOptions}
                  title={"Status"}
                />
              </div>
              <div
                className={classNames(
                  isEdit ? "hidden" : "col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4"
                )}
              >
                <div>
                  <InputTextOnly
                    label="Password"
                    name="password"
                    onChange={handleInputChange}
                    value={values.password}
                    labelOn={true}
                    error={errors.password ?? undefined}
                    type="password"
                  />
                </div>
                <div>
                  <InputTextOnly
                    label="Confirm password"
                    name="password_confirmation"
                    onChange={handleInputChange}
                    value={values.password_confirmation}
                    labelOn={true}
                    error={errors.password_confirmation ?? undefined}
                    type="password"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="sm:flex-no-wrap">
            <div className="my-auto flex items-center justify-between w-full">
              <div>
                <button
                  type="submit"
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-main-purple hover:bg-indigo-700 items-center mt-3"
                >
                  {isEdit ? "Save" : "Submit"}
                </button>
              </div>

              {isEdit && (
                <div>
                  <button
                    type="button"
                    onClick={openResetModal}
                    className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-main-purple hover:bg-indigo-700 items-center mt-3"
                  >
                    Reset Password
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Reset Password Modal */}
          {showResetModal ? (
            <div className="fixed z-50 inset-0 overflow-y-auto">
              <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div
                  className="fixed inset-0 transition-opacity"
                  aria-hidden="true"
                  onClick={() => setShowResetModal(false)}
                >
                  <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                </div>

                <span
                  className="hidden sm:inline-block sm:align-middle sm:h-screen"
                  aria-hidden="true"
                >
                  &#8203;
                </span>

                <div
                  className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div>
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Reset Password</h3>
                    <div className="mt-4">
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Password
                          </label>
                          <input
                            name="password"
                            type="password"
                            value={resetValues.password}
                            onChange={handleResetChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                          />
                          {resetErrors.password && (
                            <p className="mt-1 text-sm text-red-600">{resetErrors.password}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Confirm Password
                          </label>
                          <input
                            name="password_confirmation"
                            type="password"
                            value={resetValues.password_confirmation}
                            onChange={handleResetChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                          />
                          {resetErrors.password_confirmation && (
                            <p className="mt-1 text-sm text-red-600">
                              {resetErrors.password_confirmation}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-6 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      onClick={handleConfirmReset}
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-main-purple text-base font-medium text-white hover:bg-indigo-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
                    >
                      Confirm
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowResetModal(false)}
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </form>
  );
}
